import { useState } from 'react';
import { useNavigate } from 'react-router';

import { Button, Card, Dropdown, Field, Input, TableFootNote } from '@/design-system';
import {
    TENANT_STATUS_LABELS,
    TENANT_STATUSES,
    type PlatformTenantFilters,
} from '@/entities/tenant';
import { Pager } from '@/shared/ui';

import { paths } from '@/app/router/paths';
import { AdminHeader } from './components/AdminHeader';
import { AdminRegion } from './components/AdminRegion';
import { TenantTable } from './components/TenantTable';
import { adminCopy } from './copy';
import { usePlatformTenants } from './hooks';

/**
 * Platformbeheer - de organisaties.
 *
 * Een lijst met zoeken en een statusfilter; een rij opent de organisatie op
 * haar eigen adres. Tegoed boeken en meekijken gebeuren op dat detail, want
 * beide horen bij precies één organisatie.
 */
export function AdminTenantsPage() {
    const navigate = useNavigate();
    const [filters, setFilters] = useState<PlatformTenantFilters>({ page: 1 });
    const tenants = usePlatformTenants(filters);
    const total = tenants.data?.meta?.total ?? tenants.data?.items.length ?? 0;

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <AdminHeader
                eyebrow={`${adminCopy.eyebrowPlatform} · ${adminCopy.tenantCount(total)}`}
                title={adminCopy.tenantsTitle}
                actions={
                    <Button
                        variant="onDark"
                        size="compact"
                        onClick={() => navigate(paths.adminNewTenant)}
                    >
                        {adminCopy.createAction}
                    </Button>
                }
            />

            <div className="flex flex-1 flex-col gap-[14px] overflow-auto bg-[var(--surface-page)] px-7 pt-[22px] pb-8">
                <Card padding="md">
                    <div className="flex flex-wrap items-end gap-2">
                        <Field
                            label={adminCopy.tenantsSearch}
                            size="sm"
                            className="min-w-[180px] flex-1"
                        >
                            <Input
                                type="search"
                                icon="search"
                                value={filters.search ?? ''}
                                placeholder={adminCopy.tenantsSearchPlaceholder}
                                onChange={(event) =>
                                    setFilters({ ...filters, search: event.target.value, page: 1 })
                                }
                            />
                        </Field>
                        <Field label={adminCopy.tenantsStatus} size="sm" className="min-w-[160px]">
                            <Dropdown
                                value={filters.status ?? ''}
                                options={[
                                    { value: '', label: adminCopy.tenantsStatusAll },
                                    ...TENANT_STATUSES.map((status) => ({
                                        value: status,
                                        label: TENANT_STATUS_LABELS[status],
                                    })),
                                ]}
                                onChange={(status) => setFilters({ ...filters, status, page: 1 })}
                            />
                        </Field>
                    </div>
                </Card>

                <AdminRegion title={adminCopy.tenantsTitle} query={tenants}>
                    {tenants.data ? (
                        <Card clip>
                            <TenantTable
                                tenants={tenants.data.items}
                                onOpen={(tenant) => navigate(paths.adminTenant(tenant.uuid))}
                            />
                            {tenants.data.items.length === 0 ? (
                                <TableFootNote>{adminCopy.tenantsEmpty}</TableFootNote>
                            ) : null}
                            <Pager
                                meta={tenants.data.meta}
                                noun={adminCopy.tenantsNoun}
                                busy={tenants.isFetching}
                                onPage={(page) => setFilters({ ...filters, page })}
                            />
                        </Card>
                    ) : null}
                </AdminRegion>
            </div>
        </div>
    );
}
